import React, { useState } from "react";
import { useDocument } from "../loan_tool";
import { useUser } from "../../../hooks/user";
import { formatPhone } from "./form_utils";
import "./css/ba_forms.scss";

const PRIVACY_NOTICE = `This information is to be used by the agency collecting it or its assignees in determining whether you qualify as a prospective mortgagor under its program. It will not be disclosed outside the agency except as required and permitted by law. You do not have to provide this information, but if you do not your application for approval as a prospective mortgagor or borrower may be delayed or rejected. The information requested in this form is authorized by Title 38, USC, Chapter 37 (if VA); by 12 USC, Section 1701 et seq. (if HUD/FHA); by 42 USC, Section 1452b (if HUD/CPD); and Title 42 USC, 1471 et seq., or 7 USC, 1921 et seq. (if USDA/FmHA).`;

export default function BorrowerAuth() {
  const [{ companyProfile }] = useUser();
  const [document, , { documentAccountProfile }] = useDocument();

  const [date, setDate] = useState("");
  const [loanNumber, setLoanNumber] = useState("");
  const [sigDates, setSigDates] = useState(["", "", "", ""]);

  const borrowers = (document.applicant && document.applicant.borrowers) || [];
  const property = document.property || {};

  const loName = [documentAccountProfile.first_name, documentAccountProfile.last_name]
    .filter(Boolean).join(" ");

  const companyName = companyProfile.company_name || "";

  const companyAddr = [
    companyProfile.business_address,
    companyProfile.business_address_2
  ].filter(Boolean).join(" ");

  const companyCityLine = [
    companyProfile.city,
    companyProfile.state
  ].filter(Boolean).join(", ") + (companyProfile.zip ? " " + companyProfile.zip : "");

  const propertyAddr = [property.address, property.address2].filter(Boolean).join(" ");
  const propertyCityLine = [property.city, property.state].filter(Boolean).join(", ") +
    (property.zip ? " " + property.zip : "");

  function updateSigDate(idx, val) {
    const next = [...sigDates];
    next[idx] = val;
    setSigDates(next);
  }

  const activeBorrowers = borrowers.filter(b => b && (b.first_name || b.last_name));

  return (
    <div className="ba-doc">
      {/* Header */}
      <div className="ba-doc-header">
        {companyProfile.company_name && (
          <>
            {companyProfile.company_name}
            {companyProfile.company_state_license && `, LIC# ${companyProfile.company_state_license}`}
            {companyProfile.company_nmls_number && `, NMLS# ${companyProfile.company_nmls_number}`}
          </>
        )}
        {loName && (
          <>
            {" | Originator: "}{loName}
            {documentAccountProfile.state_real_estate_license && `, LIC# ${documentAccountProfile.state_real_estate_license}`}
            {documentAccountProfile.nmls_number && `, NMLS# ${documentAccountProfile.nmls_number}`}
          </>
        )}
      </div>

      <div className="ba-doc-title">Borrower Signature Authorization</div>

      {/* Privacy Act Notice */}
      <div className="ba-notice-box">
        <strong>Privacy Act Notice: </strong>{PRIVACY_NOTICE}
      </div>

      {/* Part I */}
      <div className="ba-section-heading">Part I - General Information</div>

      <table className="ba-info-table">
        <tbody>
          <tr>
            <td className="ba-td-half">
              <div className="ba-cell-label">1. Borrower(s)</div>
              <div className="ba-cell-value">
                {activeBorrowers.length > 0 ? activeBorrowers.map((b, i) => (
                  <div key={i}>
                    {[b.first_name, b.middle_name, b.last_name].filter(Boolean).join(" ")}
                  </div>
                )) : <div className="ba-placeholder">No borrowers on file</div>}
                {propertyAddr && <div className="ba-addr-line">{propertyAddr}</div>}
                {propertyCityLine && <div className="ba-addr-line">{propertyCityLine}</div>}
              </div>
            </td>
            <td className="ba-td-half">
              <div className="ba-cell-label">2. Name and address of Lender/Broker</div>
              <div className="ba-cell-value">
                {companyName && <div><strong>{companyName}</strong></div>}
                {companyAddr && <div>{companyAddr}</div>}
                {companyCityLine && <div>{companyCityLine}</div>}
                {companyProfile.contact_phone && (
                  <div>TEL: {formatPhone(companyProfile.contact_phone, companyProfile.extension)}</div>
                )}
              </div>
            </td>
          </tr>
          <tr>
            <td className="ba-td-half">
              <div className="ba-cell-label">3. Date</div>
              <input
                className="ba-table-input"
                type="text"
                placeholder="MM/DD/YYYY"
                value={date}
                onChange={e => setDate(e.target.value)}
              />
              <div className="ba-field-hint">Fill in date field</div>
            </td>
            <td className="ba-td-half">
              <div className="ba-cell-label">4. Loan Number</div>
              <input
                className="ba-table-input"
                type="text"
                placeholder="Loan number"
                value={loanNumber}
                onChange={e => setLoanNumber(e.target.value)}
              />
              <div className="ba-field-hint">Fill in loan number</div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Part II */}
      <div className="ba-section-heading">Part II - Borrower Authorization</div>

      <p className="ba-legal-text">
        I hereby authorize the Lender/Broker to verify my past and present employment earnings records, bank accounts,
        stock holdings, and any other asset balances that are needed to process my mortgage loan application. I further
        authorize the Lender/Broker to order a consumer credit report and verify other credit information, including
        past and present mortgage and landlord references. It is understood that a copy of this form will also serve
        as authorization.
      </p>

      <p className="ba-legal-text">
        The information the Lender/Broker obtains is only to be used in the processing of my application for a
        mortgage loan.
      </p>

      {/* Signature Lines — always 4 rows (Borrower + up to 3 Co-Borrowers) */}
      <div className="ba-sig-block">
        {[0, 1, 2, 3].map(i => {
          const b = borrowers[i] || {};
          const label = i === 0 ? "Borrower" : `Co-Borrower ${i}`;
          const name = [b.first_name, b.middle_name, b.last_name].filter(Boolean).join(" ");
          return (
            <div className="ba-sig-row" key={i}>
              <div className="ba-sig-left">
                <div className="ba-sig-name-line">{name}</div>
                <div className="ba-sig-label">{label}</div>
              </div>
              <div className="ba-sig-right">
                <input
                  className="ba-sig-date-input"
                  type="text"
                  placeholder="MM/DD/YYYY"
                  value={sigDates[i]}
                  onChange={e => updateSigDate(i, e.target.value)}
                />
                <div className="ba-sig-label">Date (MM/DD/YYYY)</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="ba-form-footer">LANIS Form - (07/01/2026)</div>
    </div>
  );
}
